import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/9944895/pexels-photo-9944895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940";

// Demo accounts shown on the login screen as "tap to sign in" buttons.
// NOT secrets — same values are documented in /app/memory/test_credentials.md and visible in the UI by design.
const PRESETS = [
  { label: "Super Admin", email: "super@nammaxfit.com", password: "Super@123" },
  { label: "Branch Admin", email: "admin@nammaxfit.com", password: "Admin@123" },
  { label: "Coach", email: "coach@nammaxfit.com", password: "Coach@123" },
  { label: "Member", email: "customer@nammaxfit.com", password: "Customer@123" },
];

export default function LoginPage() {
  const { user, login, loginGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Handle Google OAuth fragment (Emergent-managed)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (sid) {
        (async () => {
          try {
            setBusy(true);
            const u = await loginGoogleSession(sid);
            toast.success(`Welcome ${u.full_name}`);
            window.history.replaceState(null, "", "/dashboard");
            navigate(u.role === "CUSTOMER" ? "/me" : "/dashboard", { replace: true });
          } catch (e) {
            toast.error("Google session failed");
          } finally {
            setBusy(false);
          }
        })();
      }
    }
  }, [loginGoogleSession, navigate]);

  useEffect(() => {
    if (user) navigate(user.role === "CUSTOMER" ? "/me" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    return doLogin(email, password);
  };

  const doLogin = async (mail, pwd) => {
    if (!mail || !pwd) {
      toast.error("Enter both email and password");
      return;
    }
    setBusy(true);
    try {
      const u = await login(mail, pwd);
      toast.success(`Welcome ${u.full_name}`);
      navigate(u.role === "CUSTOMER" ? "/me" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirect = encodeURIComponent(window.location.origin + "/login");
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  // One-click sign-in: fill the visible fields AND submit
  const fillPreset = (p) => {
    setEmail(p.email);
    setPassword(p.password);
    doLogin(p.email, p.password);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background" data-testid="login-page">
      {/* Hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_IMG} alt="Athlete climbing rope" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-volt grid place-items-center rounded-sm">
              <span className="font-display text-2xl font-black text-black leading-none">N</span>
            </div>
            <div>
              <div className="font-display text-2xl font-black uppercase tracking-tight">NammaXFit</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">Bengaluru's Strength Lab</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-volt mb-3">Command Center</div>
            <h1 className="font-display text-6xl xl:text-7xl font-black uppercase leading-[0.9] tracking-tight">
              Run your gym<br />like a <span className="text-volt">tactical unit.</span>
            </h1>
            <p className="mt-6 text-zinc-300 max-w-md leading-relaxed">
              Sales pipeline. Coach accountability. Retention scoring. Capacity utilization.
              One operating system for every NammaXFit branch.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <Stat label="Branches" value="3" />
              <Stat label="Active" value="210" />
              <Stat label="Utilization" value="43%" />
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">www.nammaxfit.com</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-grid">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-volt mb-2">Sign in</div>
            <h2 className="font-display text-4xl font-black uppercase tracking-tight">Access Command</h2>
            <p className="text-zinc-500 text-sm mt-2">Staff use Google. Members use email & password.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-zinc-400">Email</Label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="bg-zinc-950 border-zinc-800 pl-9 h-11 rounded-sm focus:border-volt focus-visible:ring-volt"
                  placeholder="you@nammaxfit.com" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-zinc-400">Password</Label>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="bg-zinc-950 border-zinc-800 pl-9 h-11 rounded-sm focus:border-volt focus-visible:ring-volt"
                  placeholder="••••••••" data-testid="login-password-input" />
              </div>
            </div>

            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full h-11 bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
              {busy ? "Signing in…" : <>Enter Command <ArrowRight size={16} className="ml-1" /></>}
            </Button>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-900" /></div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                <span className="bg-background px-2">or</span>
              </div>
            </div>

            <Button type="button" variant="outline" onClick={googleLogin}
              className="w-full h-11 bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700 rounded-sm uppercase tracking-wider"
              data-testid="google-login-button">
              Continue with Google (Staff)
            </Button>
          </form>

          {/* Demo presets */}
          <div className="mt-8 pt-6 border-t border-zinc-900">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3 font-semibold">Demo accounts — tap to sign in</div>
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button key={p.email} type="button" onClick={() => fillPreset(p)}
                  disabled={busy}
                  data-testid={`preset-${p.label.toLowerCase().replace(" ", "-")}`}
                  className="text-left bg-zinc-950 border border-zinc-900 hover:border-volt/50 hover:bg-zinc-900 px-3 py-2 rounded-sm transition-colors disabled:opacity-50">
                  <div className="text-xs font-semibold">{p.label}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{p.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-4xl font-black text-volt leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
